/*
  INTOVENT スライダー。
  Dawnの slideshow-component はスクロール位置ベースのため、最後のスライドから
  先頭へ戻るときに必ず右方向へ巻き戻る。常に同じ方向へ流し続けたいので、
  このセクション専用に実装している。

  - slide: トラックをtranslateXで動かす。先頭スライドの複製を末尾に足しておき、
           複製まで進んだらトランジションを切って先頭へ瞬時に戻すことで、
           見た目上ループさせる。
  - fade : スライドを重ねて opacity で切り替える。位置を持たないので複製は不要。
*/
class IntoventSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.intovent-slider__track');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('.intovent-slider__slide'));
    this.slideCount = this.slides.length;
    if (this.slideCount === 0) return;

    this.mode = this.dataset.mode === 'fade' ? 'fade' : 'slide';
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseFloat(this.dataset.speed) || 5) * 1000;
    this.index = 0;
    this.isAnimating = false;

    this.dots = Array.from(this.querySelectorAll('.intovent-slider__dot'));
    this.prevButton = this.querySelector('.intovent-slider__arrow--prev');
    this.nextButton = this.querySelector('.intovent-slider__arrow--next');

    if (this.mode === 'slide' && this.slideCount > 1) {
      const clone = this.slides[0].cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.add('intovent-slider__slide--clone');
      this.track.appendChild(clone);
      this.track.addEventListener('transitionend', this.onTransitionEnd.bind(this));
    }

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });
    if (this.prevButton) this.prevButton.addEventListener('click', () => this.previous());
    if (this.nextButton) this.nextButton.addEventListener('click', () => this.next());

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.play());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', () => this.play());
    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.pause() : this.play();
    });

    this.render(false);
    this.play();
  }

  disconnectedCallback() {
    this.pause();
  }

  play() {
    if (!this.autoplayEnabled || this.slideCount < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.pause();
    this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
  }

  pause() {
    clearInterval(this.autoplayTimer);
  }

  next() {
    if (this.slideCount < 2 || this.isAnimating) return;

    if (this.mode === 'fade') {
      this.index = (this.index + 1) % this.slideCount;
    } else {
      // 末尾は複製スライド。そこまで進んでから先頭へ瞬時に戻す。
      this.index += 1;
      this.isAnimating = true;
    }
    this.render(true);
  }

  previous() {
    if (this.slideCount < 2 || this.isAnimating) return;

    if (this.mode === 'fade') {
      this.index = (this.index - 1 + this.slideCount) % this.slideCount;
      this.render(true);
      return;
    }

    if (this.index === 0) {
      // 一旦アニメーションなしで複製の位置へ飛ばしてから、左へ戻す。
      this.index = this.slideCount;
      this.render(false);
      this.reflow();
    }
    this.index -= 1;
    this.isAnimating = true;
    this.render(true);
  }

  goTo(index) {
    if (this.isAnimating || index === this.index % this.slideCount) return;
    this.index = index;
    this.render(true);
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    this.isAnimating = false;

    if (this.index === this.slideCount) {
      this.index = 0;
      this.render(false);
      this.reflow();
    }
  }

  // transitionを切り替えた直後に位置を確定させ、意図しないアニメーションを防ぐ。
  reflow() {
    void this.track.offsetHeight;
    this.track.style.transition = '';
  }

  render(animate) {
    const activeIndex = this.index % this.slideCount;

    if (this.mode === 'fade') {
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === activeIndex);
        slide.setAttribute('aria-hidden', i === activeIndex ? 'false' : 'true');
      });
    } else {
      this.track.style.transition = animate ? '' : 'none';
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
    }

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('intovent-slider__dot--active', i === activeIndex);
      dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
    });
  }
}

customElements.define('intovent-slider', IntoventSlider);
